import { useEffect, useState, useCallback } from 'react';
import {
  getCurrentUser,
  signInWithRedirect,
  signOut as amplifySignOut,
  fetchAuthSession
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  email: string | null;
}

// Dev-mode bypass: when running `vite dev` with VITE_DEV_AUTH=1, skip Cognito
// entirely and return a synthetic admin session. Used for local development
// and Playwright e2e tests so we don't need a real OAuth flow on every run.
// Hard-disabled in production builds (import.meta.env.DEV is false).
const DEV_AUTH_BYPASS =
  import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH === '1';

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: DEV_AUTH_BYPASS,
    isLoading: !DEV_AUTH_BYPASS,
    email: DEV_AUTH_BYPASS ? 'dev@bws.ninja' : null
  });

  const checkAuth = useCallback(async () => {
    if (DEV_AUTH_BYPASS) return;
    try {
      await getCurrentUser();
      const session = await fetchAuthSession();
      const claims = session.tokens?.idToken?.payload;
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        email: (claims?.email as string) ?? null
      });
    } catch {
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        email: null
      });
    }
  }, []);

  useEffect(() => {
    if (DEV_AUTH_BYPASS) return;

    // If returning from OAuth redirect, Amplify handles the code exchange
    // automatically before our checkAuth runs. Give it a moment to process.
    const params = new URLSearchParams(window.location.search);
    const hasCode = params.has('code');

    if (hasCode) {
      const timeout = setTimeout(() => {
        void checkAuth();
      }, 5000);

      const unsubscribe = Hub.listen('auth', ({ payload }) => {
        clearTimeout(timeout);
        if (payload.event === 'signedIn') {
          void checkAuth();
        }
        if (payload.event === 'signInWithRedirect_failure') {
          window.history.replaceState({}, '', window.location.pathname);
          setAuthState({ isAuthenticated: false, isLoading: false, email: null });
        }
      });

      return () => {
        clearTimeout(timeout);
        unsubscribe();
      };
    }

    void checkAuth();

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn' || payload.event === 'signedOut') {
        void checkAuth();
      }
    });

    return unsubscribe;
  }, [checkAuth]);

  const signInWithGoogle = async () => {
    if (DEV_AUTH_BYPASS) return;
    try {
      await signInWithRedirect({ provider: 'Google' });
    } catch {
      try {
        await amplifySignOut();
      } catch { /* ignore */ }
      await signInWithRedirect({ provider: 'Google' });
    }
  };

  const signOut = async () => {
    if (DEV_AUTH_BYPASS) {
      // No-op in dev — pretend signed out then signed back in next render
      return;
    }
    try {
      await amplifySignOut({ global: true });
    } catch { /* ignore */ }
    setAuthState({ isAuthenticated: false, isLoading: false, email: null });
  };

  return {
    ...authState,
    signInWithGoogle,
    signOut
  };
}
